import type { FormProps } from "../types";
import DynamicMultiStepForm from "../dynamic-multi-step-form";
import {
  API_ENDPOINTS,
  oauth_domain,
  oauth_key,
} from "../../../../constants";
import { getSessionId, getCookie } from "../../../../libs/fns";
import { useBottomSheet } from "../../../../contexts/bottom-sheet";
import {
  buildCustomerName,
  buildCustomerContact,
  buildSubmissionPayload,
} from "../../../../libs/formHelpers";

type Props = {
  formProps: FormProps;
};

export default function ContactForm({ formProps }: Props) {
  const { openBottomSheet } = useBottomSheet();
  async function callback(values: Record<string, unknown>) {
    const session_id = getSessionId();
    if (!session_id || !oauth_key || !oauth_domain) {
      console.error("[ContactForm] セッション情報がありません。");
      alert("セッション情報が見つかりません。ページを再読み込みしてください。");
      return;
    }

    try {
      // CSRF token取得
      const csrftoken = getCookie("csrftoken");

      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      if (csrftoken) {
        headers["X-CSRFToken"] = csrftoken;
      }

      // フォームデータから不要なフィールドを除外
      const { uid: _uid, ...cleanValues } = values as any;
      const stringValues = Object.fromEntries(
        Object.entries(cleanValues).map(([k, v]) => [k, String(v ?? "")])
      ) as Record<string, string>;

      // フォーム構造を使って顧客情報・ペイロードを動的に生成
      const form_data = buildSubmissionPayload(stringValues, formProps);
      const customer_name = buildCustomerName(stringValues, formProps);
      const { mail: customer_mail, phone: customer_phone } = buildCustomerContact(stringValues, formProps);

      const requestBody = {
        oauth_key,
        oauth_domain,
        session_id,
        form_data,
        customer_name,
        customer_mail,
        customer_phone,
      };

      // requestBodyをJSON文字列化
      const requestBodyString = JSON.stringify(requestBody);

      // API送信データログ
      console.log("[ContactForm API] 送信データ:", {
        endpoint: API_ENDPOINTS.get_form_data,
        form_data,
        customer_name,
        customer_mail,
        customer_phone,
      });

      const response = await fetch(API_ENDPOINTS.get_form_data, {
        method: "POST",
        headers,
        body: requestBodyString,
        // oauth_key/oauth_domainで認証する公開ウィジェットAPIのためCookie不要
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[ContactForm] API Error Response:", errorText);
        console.error("[ContactForm] Status:", response.status);
        console.error("[ContactForm] 送信したデータ:", requestBody);
        try {
          const errorJson = JSON.parse(errorText);
          console.error("[ContactForm] エラー詳細:", errorJson);
        } catch (e) {
          // JSON parse error
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      // APIレスポンスログ
      console.log("[ContactForm API] レスポンス受信成功:", responseData);

      // 送信完了ページに遷移（Contextを使用）
      openBottomSheet("THANKS");
    } catch (error) {
      console.error("[ContactForm] フォーム送信失敗:", error);
      alert("フォームの送信に失敗しました。もう一度お試しください。");
    }
  }

  return (
    <DynamicMultiStepForm
      formProps={formProps}
      callback={callback}
    />
  );
}
